import {
  type PDPContextType,
  usePDPContext,
} from '@/app/[locale]/product/_contexts/PDPProvider';
import { ChargerModelEnum } from '@/app/enums/ChargerModelEnum';
import { RingFinishEnum } from '@/app/enums/RingFinishEnum';
import type { ChargerModel } from '@/app/types/accessory';
import type { ListProduct } from '@/queries/Products';

import filterByInStock from '../_utils/filterByInStock';
import type UseProducts from './types';
import type { Ring } from './types';
import type CurrentRouteValues from './useCurrentRoute/types';

const useProducts = ({
  accessory,
  isOuraRing4Wizard,
  style: routeStyle,
  finish: routeFinish,
}: Pick<
  CurrentRouteValues,
  'accessory' | 'isOuraRing4Wizard' | 'finish' | 'style'
>): UseProducts => {
  const { data, getStyleStockStatus, getFinishStockStatus }: PDPContextType =
    usePDPContext();

  const ringFinish =
    routeFinish.value.length > 0 ? routeFinish.value : RingFinishEnum.Silver;

  const subscription: ListProduct | undefined = data?.find((product) => {
    const handle = product.handle;
    return handle === 'subscription';
  });

  const oneYearMembership: ListProduct | undefined = data?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'one-year-membership',
  );

  const twoYearWarranty: ListProduct | undefined = data?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-2year',
  );

  const threeYearWarranty: ListProduct | undefined = data?.find(
    ({ handle }: { handle: ListProduct['handle'] }) =>
      handle === 'extended-warranty-3year',
  );

  const charger: ListProduct | undefined = data?.find(
    (product: ListProduct) => {
      const handle = product.handle as ChargerModel;
      return handle === accessory.value;
    },
  );

  // used by chargers model step
  const chargers: ListProduct[] | undefined = data
    ?.filter((product) => {
      const handle = product.handle as ChargerModel;

      return (
        handle === ChargerModelEnum.ChargerOura3 ||
        handle === ChargerModelEnum.ChargerOura4
      );
    })
    .sort((a, b) => b.handle.localeCompare(a.handle));

  const { filteredStyleByInStock, filteredFinishByInStock } = filterByInStock({
    productsData: data,
    isOuraRing4Wizard,
    getStyleStockStatus,
    getFinishStockStatus,
  });

  // used by finish step
  const ringsByStyle: Ring[] | undefined = filteredFinishByInStock?.filter(
    ({ style }) => style === routeStyle.value,
  );

  /* used by charger size step carousel,
   * charger size step size selectors,
   * ring finish step image,
   * ring size step image, and adding to cart
   */
  const product: ListProduct | undefined =
    charger ??
    filteredStyleByInStock?.find(
      ({ style, finish }) =>
        style === routeStyle.value && finish === ringFinish,
    );

  return {
    chargers,
    filteredFinishByInStock,
    oneYearMembership,
    product,
    rings: filteredStyleByInStock,
    ringsByStyle,
    subscription,
    threeYearWarranty,
    twoYearWarranty,
  };
};

export default useProducts;
